import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { usuarioPodeAtenderVendas, usuarioPodeUsarChecklists, usuarioTemAcessoTela } from "../utils/permissoes";
import { normalizarChaveTela } from "../layout/menuAcesso";
import { obterTodasChavesTelasDisponiveis } from "../layout/telasDisponiveis";
import { useConfiguracoesSistemaPublicas } from "../utils/configuracoesSistema";
import { calcularRotaInicial } from "./rotaInicial";

/**
 * Protege rotas autenticadas. Enquanto valida a sessão (/api/auth/me) mostra um
 * carregando; sem sessão redireciona para /login guardando a rota de origem.
 * Também bloqueia navegação direta por URL para telas fora de `telasAcesso`
 * do perfil: em vez de abrir uma tela de "acesso negado", devolve o usuário
 * para a rota inicial permitida a ele (os botões do menu já ficam desabilitados,
 * então isso só acontece por URL digitada ou link antigo).
 */
export function RotaProtegida({ children }: { children: ReactNode }) {
  const { usuario, carregando } = useAuth();
  const configuracoes = useConfiguracoesSistemaPublicas();
  const location = useLocation();

  if (carregando) {
    return (
      <div
        style={{
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--muted)",
        }}
      >
        Carregando…
      </div>
    );
  }

  if (!usuario) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const chaveTela = normalizarChaveTela(location.pathname);
  const rotaAtendimentoVendas = chaveTela === "vendas/atendimento";
  const rotaChecklistSuporte = chaveTela === "suporte/checklists" || chaveTela.startsWith("suporte/checklists/");
  if (rotaAtendimentoVendas && !usuarioPodeAtenderVendas(usuario)) {
    const rotaPermitida = calcularRotaInicial(usuario, configuracoes);
    if (!rotaPermitida) return location.pathname === "/" ? <>{children}</> : <Navigate to="/" replace />;
    if (rotaPermitida === location.pathname) return <>{children}</>;
    return <Navigate to={rotaPermitida} replace />;
  }
  if (rotaChecklistSuporte && !usuarioPodeUsarChecklists(usuario)) {
    const rotaPermitida = calcularRotaInicial(usuario, configuracoes);
    if (!rotaPermitida) return location.pathname === "/" ? <>{children}</> : <Navigate to="/" replace />;
    if (rotaPermitida === location.pathname) return <>{children}</>;
    return <Navigate to={rotaPermitida} replace />;
  }
  const telaControlada = obterTodasChavesTelasDisponiveis(configuracoes).includes(chaveTela);
  if (!rotaAtendimentoVendas && !rotaChecklistSuporte && telaControlada && !usuarioTemAcessoTela(usuario, chaveTela)) {
    const rotaPermitida = calcularRotaInicial(usuario, configuracoes);
    /* Sem nenhuma rota permitida (perfil sem telas liberadas) não há para onde
       mandar: renderiza vazio em vez de entrar em um laço de redirecionamento. */
    if (!rotaPermitida) return location.pathname === "/" ? <>{children}</> : <Navigate to="/" replace />;
    if (rotaPermitida === location.pathname) return <>{children}</>;
    return <Navigate to={rotaPermitida} replace />;
  }

  return <>{children}</>;
}
